// Cognito Hosted UI auth with PKCE for seamless SSO across admin and notes apps.
// After a successful callback, tokens are stored in the amazon-cognito-identity-js
// localStorage format so pool.getCurrentUser().getSession() continues to work.

const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || 'hca-auth.auth.us-east-2.amazoncognito.com';
const CLIENT_ID      = process.env.REACT_APP_USER_POOL_CLIENT_ID;
export const POOL_PREFIX = `CognitoIdentityServiceProvider.${CLIENT_ID}`;

function b64url(bytes) {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function buildPKCE() {
  const verifier  = b64url(crypto.getRandomValues(new Uint8Array(32)));
  const hash      = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = b64url(new Uint8Array(hash));
  return { verifier, challenge };
}

function decodeJWT(token) {
  const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(b64));
}

// Redirect to Cognito hosted UI. Pass silent=true for a transparent SSO attempt
// (Cognito returns login_required instead of showing the login page if no session).
export async function redirectToHostedUI({ silent = false, returnPath = '/dashboard' } = {}) {
  const { verifier, challenge } = await buildPKCE();
  sessionStorage.setItem('pkce_verifier', verifier);
  sessionStorage.setItem('pkce_return',   returnPath);
  const p = new URLSearchParams({
    response_type:         'code',
    client_id:             CLIENT_ID,
    redirect_uri:          `${window.location.origin}/callback`,
    scope:                 'openid email profile',
    code_challenge_method: 'S256',
    code_challenge:        challenge,
    ...(silent ? { prompt: 'none' } : {}),
  });
  window.location.assign(`https://${COGNITO_DOMAIN}/oauth2/authorize?${p}`);
}

// Call this from /callback route. Exchanges the auth code for tokens, stores them
// in localStorage in the amazon-cognito-identity-js format, and returns returnPath.
// Throws 'login_required' if a silent-auth attempt failed (no existing Cognito session).
export async function handleOAuthCallback() {
  const params     = new URLSearchParams(window.location.search);
  const code       = params.get('code');
  const error      = params.get('error');
  const verifier   = sessionStorage.getItem('pkce_verifier');
  const returnPath = sessionStorage.getItem('pkce_return') || '/dashboard';
  sessionStorage.removeItem('pkce_verifier');
  sessionStorage.removeItem('pkce_return');

  if (error) throw new Error(error);
  if (!verifier) throw new Error('login_required');

  const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body:    new URLSearchParams({
      grant_type:    'authorization_code',
      client_id:     CLIENT_ID,
      redirect_uri:  `${window.location.origin}/callback`,
      code,
      code_verifier: verifier,
    }),
  });
  if (!res.ok) throw new Error(await res.text());

  const { id_token, access_token, refresh_token } = await res.json();
  const payload  = decodeJWT(id_token);
  const username = payload['cognito:username'];

  localStorage.setItem(`${POOL_PREFIX}.LastAuthUser`,             username);
  localStorage.setItem(`${POOL_PREFIX}.${username}.idToken`,      id_token);
  localStorage.setItem(`${POOL_PREFIX}.${username}.accessToken`,  access_token);
  localStorage.setItem(`${POOL_PREFIX}.${username}.refreshToken`, refresh_token);
  localStorage.setItem(`${POOL_PREFIX}.${username}.clockDrift`,   '0');

  return { returnPath, username, payload };
}

// Clear local tokens and redirect to Cognito logout endpoint, which clears the
// Cognito session cookie so the SSO session is fully terminated. The refresh
// token is revoked first (best effort, keepalive so it survives the redirect)
// so a copy of it cannot mint new tokens after sign-out.
export function hostedUILogout() {
  const username = localStorage.getItem(`${POOL_PREFIX}.LastAuthUser`);
  if (username) {
    const refreshToken = localStorage.getItem(`${POOL_PREFIX}.${username}.refreshToken`);
    if (refreshToken) {
      try {
        fetch(`https://${COGNITO_DOMAIN}/oauth2/revoke`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ token: refreshToken, client_id: CLIENT_ID }),
          keepalive: true,
        }).catch(() => {});
      } catch { /* revoke is best effort */ }
    }
    ['idToken', 'accessToken', 'refreshToken', 'clockDrift'].forEach(k =>
      localStorage.removeItem(`${POOL_PREFIX}.${username}.${k}`)
    );
    localStorage.removeItem(`${POOL_PREFIX}.LastAuthUser`);
  }
  const p = new URLSearchParams({ client_id: CLIENT_ID, logout_uri: window.location.origin });
  window.location.assign(`https://${COGNITO_DOMAIN}/logout?${p}`);
}
